import classNames from 'classnames';
import escapeHTML from 'escape-html';
import { FC, Fragment, ReactNode } from 'react';
import { Text } from 'slate';
import { BlockQuote, H1, H2, H3, H4, H5, H6, OrderedList, TypeLink, TypeProps } from './typography';
import compact from 'lodash/compact';
import { Quote } from 'lucide-react';
const addBreaks = (text: string) => {
  return text.split('\n').join('<br />');
};
type Tag = 'link' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'blockquote' | 'ol';
export type ComponentMap = Record<Tag | string, (node: any, key: string, serialize: Serializer, typeOptions?: TypeProps) => ReactNode>;
const defaultComponentMap: ComponentMap = {
  link: (node, key, serialize, typeOptions) => node.url && <TypeLink key={key} href={node.url}>
        {serialize(node.children, node, typeOptions)}
      </TypeLink>,
  h1: (node, key, serialize, typeOptions) => <H1 key={key} {...typeOptions}>
      {serialize(node.children, node)}
    </H1>,
  h2: (node, key, serialize, typeOptions) => <H2 key={key} {...typeOptions}>
      {serialize(node.children, node)}
    </H2>,
  h3: (node, key, serialize, typeOptions) => <H3 key={key} {...typeOptions}>
      {serialize(node.children, node)}
    </H3>,
  h4: (node, key, serialize, typeOptions) => <H4 key={key} {...typeOptions}>
      {serialize(node.children, node)}
    </H4>,
  h5: (node, key, serialize, typeOptions) => <H5 key={key} {...typeOptions}>
      {serialize(node.children, node)}
    </H5>,
  h6: (node, key, serialize, typeOptions) => <H6 key={key} {...typeOptions}>
      {serialize(node.children, node)}
    </H6>,
  blockquote: (node, key, serialize, typeOptions) => {
    const variant = node.textAlign ? 'impact' : 'line';
    return <BlockQuote key={key} className={classNames({
      'm-16 w-full md:max-w-[75%] lg:clear-both mx-auto flex items-center space-x-4 md:space-x-8 lg:space-x-12': variant === 'impact'
    })} variant={variant} {...typeOptions}>
        {variant === 'impact' ? <div className="rounded-full border bg-white border-zinc-300 w-24 md:w-40 lg:w-64 aspect-square flex items-center justify-center lg:shrink-0">
            <Quote fill="currentColor" stroke="0" className="w-[35%] h-[35%]" />
          </div> : null}
        {serialize(node.children, node)}
      </BlockQuote>;
  },
  ol: (node, key, serialize, typeOptions) => <OrderedList key={key} {...typeOptions}>
      {serialize(node.children, node)}
    </OrderedList>
};
const serialize = (children: any[], parentNode?: any | null, typeOptions: TypeProps = {}, componentMap?: Partial<ComponentMap>): ReactNode[] => {
  const finalComponentMap = componentMap ? Object.assign({}, defaultComponentMap, componentMap) : defaultComponentMap;
  const finalSerialize = (children: any[], parentNode?: any | null, typeOptions: TypeProps = {}) => serialize(children, parentNode, typeOptions, finalComponentMap);
  return compact((children ?? []).map((node, i) => {
    if (!node) {
      return null;
    }
    if (Text.isText(node)) {
      if (!node.text || node.text.trim() === '') {
        return null;
      }
      let text = <span dangerouslySetInnerHTML={{
        __html: addBreaks(escapeHTML(node.text))
      }} />;
      if ((node as any).bold) {
        text = <span className={classNames('font-bold font-barlow', {
          'text-base md:text-lg lg:text-xl': !parentNode?.type?.startsWith('h')
        })} key={i}>
              {text}
            </span>;
      }
      return <Fragment key={i}>{text}</Fragment>;
    }
    const componentMapper = finalComponentMap[node.type as keyof ComponentMap];
    if (componentMapper) {
      return componentMapper(node, String(i), finalSerialize, typeOptions);
    }
    const childContent = serialize(node.children, node, typeOptions, finalComponentMap);
    return childContent && childContent.length > 0 ? <p key={i} className="mb-4 lg:mb-8 last:mb-0">
          {childContent}
        </p> : null;
  }));
};
type Serializer = typeof serialize;
export type RichTextProps = {
  className?: string;
  content?: any;
  typeOptions?: TypeProps;
  componentMap?: Partial<ComponentMap>;
};
export const RichText: FC<RichTextProps> = ({
  className,
  content,
  typeOptions,
  componentMap
}) => {
  return <div className={classNames('@container', className)} data-sentry-component="RichText" data-sentry-source-file="rich-text.tsx">
      <div className="text-base @md:text-lg @lg:text-xl">
        {content ? serialize(content, null, typeOptions, componentMap) : null}
      </div>
    </div>;
};